<template>
  <UDrawer
    direction="right"
    :open="open"
    :ui="{
      content: 'h-screen w-screen max-w-full rounded-none',
      container: 'h-full w-full px-0 pt-0',
      handle: 'hidden!',
      body: 'rounded-tl-none ring-0',
    }"
    :dismissible="false"
  >
    <UButton
      color="white"
      variant="outline"
      icon="i-local-ellipsis"
      @click="emit('toggled', true)"
    />

    <template #body>
      <SPMobileModalHeader
        @toggled="emit('toggled', $event)"
        @city-change="selectCity"
      />

      <template v-if="!isCitySelectorVisible">
        <div class="mb-4 bg-background-bg1">
          <UContainer>
            <UButton
              class="w-full px-0"
              :class="ui?.cityButton"
              variant="ghost"
              color="neutral"
              icon="i-local-navigation"
              trailing-icon="i-local-chevron-right"
              :ui="{
                base: 'typo-heading-h6! gap-3 py-4 rounded-none',
                leadingIcon: 'text-accent-default w-4.75 h-4.75',
                trailingIcon: 'ml-auto w-6 h-6',
              }"
              @click="isCitySelectorVisible = true"
            >
              {{ selectedCity?.label }}
            </UButton>
          </UContainer>
        </div>

        <div class="mb-10">
          <UContainer v-for="(menu, index) in FOOTER_MENU" :key="index">
            <UCollapsible
              class="border-b border-b-border-light"
              :class="ui?.menuCollapsible"
            >
              <UButton
                class="group w-full px-0"
                variant="ghost"
                color="neutral"
                trailing-icon="i-local-chevron-right"
                :ui="{
                  base: 'gap-3 py-4 typo-heading-h6! rounded-none',
                  trailingIcon:
                    'ml-auto w-6 h-6 group-data-[state=open]:rotate-90 transition-transform duration-200',
                }"
              >
                {{ menu.title }}
              </UButton>

              <template #content>
                <nuxt-link
                  v-for="({ label, href }, linkIndex) in menu.links"
                  :key="linkIndex"
                  :to="href"
                  class="block typo-paragraph-sm-medium text-neutral-light mb-2.5"
                >
                  {{ label }}
                </nuxt-link>
              </template>
            </UCollapsible>
          </UContainer>
        </div>

        <UContainer class="mb-10">
          <div class="mb-7 max-md:mb-5">
            <a
              class="block typo-paragraph-xl-medium text-content-default mb-1"
              :class="ui?.phone"
              :href="`tel:${ORGANIZATION_DATA.phone}`"
            >
              {{ ORGANIZATION_DATA.phone }}
            </a>
            <span
              class="block typo-paragraph-sm-regular text-content-default-secondary"
            >
              {{ ORGANIZATION_DATA.workTime }}
            </span>
          </div>
          <a
            class="block typo-paragraph-xl-medium text-content-default"
            :class="ui?.email"
            :href="`mailto:${ORGANIZATION_DATA.email}`"
          >
            {{ ORGANIZATION_DATA.email }}
          </a>
        </UContainer>
        <UContainer>
          <div
            class="mb-4 flex max-md:flex-col items-center justify-between gap-5 rounded-[16px] border border-neutral-border-default p-7"
            :class="ui?.feedbackCard"
          >
            <div>
              <p class="typo-paragraph-md-bold text-neutral-default mb-1.5">
                Есть предложения по развитию или сотрудничеству?
              </p>
              <p class="typo-paragraph-sm-regular! text-neutral-light">
                Напишите нам и мы свяжемся с вами в ближайшее время
              </p>
            </div>
            <div class="max-md:w-full">
              <UButton
                class="min-h-10 w-full min-w-32.25 shrink-0 grow justify-center"
                @click="openFeedbackModal"
              >
                Написать нам
              </UButton>
            </div>
          </div>
          <div
            class="flex max-md:flex-col items-center max-md:items-start justify-between gap-5 rounded-[16px] border border-neutral-border-default p-7"
            :class="ui?.socialCard"
          >
            <div>
              <p class="typo-paragraph-md-bold text-neutral-default mb-1.5">
                Спорт портал в социальных сетях
              </p>
              <p class="typo-paragraph-sm-regular text-neutral-light">
                Активно ведем и рассказываем много интересного
              </p>
            </div>

            <div class="flex gap-2">
              <a href="#">
                <img src="~/assets/icons/max.svg" alt="max ссылка" />
              </a>
              <a href="#">
                <img src="~/assets/icons/vk.svg" alt="vk ссылка" />
              </a>
              <a href="#">
                <img src="~/assets/icons/telegram.svg" alt="telegram ссылка" />
              </a>
            </div>
          </div>
        </UContainer>
      </template>

      <template v-else>
        <div class="mb-4 bg-background-bg1">
          <UContainer>
            <UButton
              class="w-full px-0"
              variant="ghost"
              color="neutral"
              icon="i-local-chevron-right"
              :ui="{
                base: 'gap-3 py-4 typo-heading-h6! rounded-none justify-start',
                leadingIcon: 'text-accent-default w-4.75 h-4.75 rotate-180',
              }"
              @click="void (isCitySelectorVisible = false)"
            >
              Выберите город
            </UButton>
          </UContainer>
        </div>

        <UContainer>
          <UInput
            v-model="query"
            class="mb-3 h-full w-full"
            icon="i-local-search"
            placeholder="Ваш город"
          />

          <URadioGroup
            v-model="city"
            class="mb-25 pl-3"
            :items="filteredCities"
            label-key="label"
            value-key="label"
            highlight
            :ui="{
              item: 'py-2.5 typo-paragraph-sm-regular!',
            }"
          />

          <UButton
            class="w-full justify-center text-center"
            @click="setCity(city as string)"
          >
            Сохранить город
          </UButton>
        </UContainer>
      </template>
    </template>
  </UDrawer>
</template>

<script lang="ts" setup>
import { FOOTER_MENU, ORGANIZATION_DATA } from '~/shared'
import FeedbackModal from './modals/FeedbackModal.vue'

defineOptions({
  name: 'SPMobileMenu',
})

const emit = defineEmits<{
  toggled: [value: boolean]
}>()

withDefaults(
  defineProps<{
    open?: boolean
    ui?: {
      cityButton?: string
      menuCollapsible?: string
      phone?: string
      email?: string
      feedbackCard?: string
      socialCard?: string
    }
  }>(),
  {
    open: true,
    ui: () => ({}),
  },
)

const directoryStore = useDirectoryStore()
const { cities, selectedCity } = storeToRefs(directoryStore)

const query = ref('')
const isCitySelectorVisible = ref(false)

const overlay = useOverlay()
const feedbackModal = overlay.create(FeedbackModal)

const filteredCities = computed(() => {
  if (!query.value) return cities.value

  return cities.value.filter((city) =>
    city.label.toLowerCase().includes(query.value.toLowerCase()),
  )
})

const city = ref(selectedCity.value?.label)

const setCity = (city: string) => {
  const selected = cities.value.find((c) => c.label === city)
  if (selected) {
    directoryStore.selectedCity = selected
    isCitySelectorVisible.value = false
  }
}

const openFeedbackModal = () => {
  feedbackModal.open()
}

const selectCity = () => {
  console.log(123123)
}
</script>

<style></style>
